import {
  createContext,
  FC,
  useCallback,
  useContext,
  useLayoutEffect,
  useState,
} from 'react';

/**
 * O que o layout precisa saber das telas Ops-Next: se há uma na rota. Com uma,
 * a área de conteúdo do Metronic (`#kt_content`) perde o recuo e a tela vai
 * até as bordas (`ops-content--bleed`, em `styles/base.scss`).
 *
 * Quem tem o provedor é o `MasterLayout` (um por layout, com e sem
 * `operation`). Duas telas ao mesmo tempo não brigam: o provedor conta quantas
 * estão montadas e a sangria fica ligada até a última sair.
 *
 * Fora de um provedor (a prévia `dev/visao-operacional`, que não usa o
 * layout) tudo isso é inócuo.
 */

const BleedContext = createContext(false);
const RegisterContext = createContext<(() => () => void) | null>(null);

export const OpsFrameProvider: FC = ({children}) => {
  const [pages, setPages] = useState(0);
  const register = useCallback(() => {
    setPages((count) => count + 1);
    return () => setPages((count) => count - 1);
  }, []);

  return (
    <RegisterContext.Provider value={register}>
      <BleedContext.Provider value={pages > 0}>
        {children}
      </BleedContext.Provider>
    </RegisterContext.Provider>
  );
};

/**
 * Liga a sangria do layout enquanto o componente está montado e `active`.
 * Em `useLayoutEffect`: o layout já sai sem o recuo na primeira pintura, sem
 * piscar a faixa cinza.
 */
export const useOpsFrameBleed = (active: boolean): void => {
  const register = useContext(RegisterContext);

  useLayoutEffect(() => {
    if (!active || !register) return undefined;
    return register();
  }, [active, register]);
};

/** A sangria está ligada? Quem lê é o layout, para a classe do `#kt_content`. */
export const useOpsFrameBleeding = (): boolean => useContext(BleedContext);
